import React, { useState, useEffect } from 'react';
import { getCustomers, createCustomer } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Phone, Mail, Package } from 'lucide-react';

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', contact_name: '', contact_phone: '', contact_email: '', notes: '' });

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      const res = await getCustomers();
      setCustomers(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.code) return toast.error('Name and code are required');
    try {
      await createCustomer(form);
      toast.success('Customer created');
      setShowCreate(false);
      setForm({ name: '', code: '', contact_name: '', contact_phone: '', contact_email: '', notes: '' });
      loadCustomers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Customers</h1>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="h-9 text-xs" style={{ backgroundColor: '#1a2fce' }} data-testid="add-customer-btn">
              <Plus size={14} className="mr-1" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Company Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="h-9 text-sm" data-testid="customer-name" /></div>
              <div><Label className="text-xs">Code * (short, unique)</Label><Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value}))} placeholder="e.g. TATA, BOSCH" className="h-9 text-sm" data-testid="customer-code" /></div>
              <div><Label className="text-xs">Contact Name</Label><Input value={form.contact_name} onChange={e => setForm(p => ({...p, contact_name: e.target.value}))} className="h-9 text-sm" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={e => setForm(p => ({...p, contact_phone: e.target.value}))} className="h-9 text-sm" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm(p => ({...p, contact_email: e.target.value}))} className="h-9 text-sm" /></div>
              <Button onClick={handleCreate} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="confirm-customer">Create Customer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Users size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No customers yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {customers.map(c => (
            <Card key={c.id} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1a2fce]/10 flex items-center justify-center shrink-0">
                  <span className="text-[#1a2fce] font-bold text-xs">{c.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{c.name}</div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {c.contact_name && <span>{c.contact_name}</span>}
                    {c.contact_phone && <span className="flex items-center gap-0.5"><Phone size={10} /> {c.contact_phone}</span>}
                    {c.contact_email && <span className="flex items-center gap-0.5"><Mail size={10} /> {c.contact_email}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-medium flex items-center gap-1"><Package size={12} /> {c.pallet_count || 0}</div>
                  <div className="text-[10px] text-gray-400">items</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
